import * as Comlink from 'comlink';

import type { CompressionProgress, PDFFile, SobZipWorkerApi } from '@/workers/sob.zip.worker';

class SobWorkerService {
  private worker: Worker | null = null;
  private workerApi: Comlink.Remote<SobZipWorkerApi> | null = null;

  /**
   * Initialize the worker
   */
  async initialize(): Promise<void> {
    if (this.worker) {
      return; // Already initialized
    }

    // Create new worker with Webpack 5 syntax
    // The worker file path must be a static string for webpack to analyze it
    this.worker = new Worker(new URL('../workers/sob.zip.worker.ts', import.meta.url), {
      type: 'module',
    });

    // Wrap with Comlink
    this.workerApi = Comlink.wrap<SobZipWorkerApi>(this.worker);

    // Test the connection
    try {
      const response = await this.workerApi.ping();
      if (response !== 'pong') {
        throw new Error('Worker ping test failed');
      }
    } catch (error) {
      this.terminate();
      throw new Error(`Failed to initialize SOB worker: ${error}`);
    }
  }

  /**
   * Compress PDFs into a ZIP file using the worker
   */
  async compressPDFs(
    pdfFiles: PDFFile[],
    onProgress?: (progress: CompressionProgress) => void,
  ): Promise<Blob> {
    if (!this.workerApi) {
      await this.initialize();
    }

    if (!this.workerApi) {
      throw new Error('Worker not initialized');
    }

    // Use Comlink.proxy to enable callback
    const progressCallback = onProgress ? Comlink.proxy(onProgress) : undefined;

    try {
      const zipBlob = await this.workerApi.compressPDFs(pdfFiles, progressCallback);

      return zipBlob;
    } catch (error) {
      console.error('Worker compression failed:', error);
      throw error;
    }
  }

  /**
   * Terminate the worker and clean up resources
   */
  terminate(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
      this.workerApi = null;
    }
  }

  /**
   * Check if worker is initialized
   */
  isInitialized(): boolean {
    return this.worker !== null && this.workerApi !== null;
  }
}

// Export singleton instance
export const sobWorkerService = new SobWorkerService();
